import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Sidebar from './Sidebar';
import './AdminUserManager.css';

const LEVELS = ['owner', 'manager', 'adviser'];

function StatusDot({ status }) {
  return <span className={"aum-dot aum-dot--" + status} title={status} />;
}

export default function AdminUserManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetError, setResetError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [roleChanging, setRoleChanging] = useState(null);

  const flash = (setter, msg) => { setter(msg); setTimeout(() => setter(''), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data.users);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      await api.patch('/admin/users/' + user.id + '/status', { status: newStatus });
      flash(setSuccess, user.name + ' ' + (newStatus === 'active' ? 'activated' : 'deactivated'));
      load();
    } catch (err) { flash(setError, err.response?.data?.error || 'Failed to update status'); }
  };

  const changeRole = async (user, broker_level) => {
    setRoleChanging(user.id);
    try {
      await api.patch('/admin/users/' + user.id + '/role', { broker_level });
      flash(setSuccess, user.name + ' is now ' + broker_level);
      load();
    } catch (err) { flash(setError, err.response?.data?.error || 'Failed to update role'); }
    finally { setRoleChanging(null); }
  };

  const assignManager = async (user, manager_id) => {
    try {
      await api.patch('/admin/users/' + user.id + '/manager', { manager_id: manager_id || null });
      flash(setSuccess, manager_id ? user.name + ' assigned to manager' : user.name + ' unassigned from manager');
      load();
    } catch (err) { flash(setError, err.response?.data?.error || 'Failed to assign manager'); }
  };

  const openReset = (user) => { setResetModal(user); setNewPassword(''); setResetError(''); setShowPw(false); };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { setResetError('Password must be at least 8 characters'); return; }
    setResetSaving(true); setResetError('');
    try {
      await api.post('/admin/users/' + resetModal.id + '/reset-password', { new_password: newPassword });
      setResetModal(null);
      flash(setSuccess, 'Password reset for ' + resetModal.name);
    } catch (err) { setResetError(err.response?.data?.error || 'Failed to reset password'); }
    finally { setResetSaving(false); }
  };

  const strength = newPassword.length >= 12 ? 'strong' : newPassword.length >= 8 ? 'ok' : 'weak';
  const allManagers = users.filter(u => u.broker_level === 'manager' || u.broker_level === 'owner');
  const owners = users.filter(u => u.broker_level === 'owner');
  const managersList = users.filter(u => u.broker_level === 'manager');
  const unassignedAdvisers = users.filter(u => (u.broker_level || 'adviser') === 'adviser' && !u.manager_id);

  const UserRow = ({ user, indented = false }) => (
    <tr className={"" + (user.status === 'inactive' ? 'aum-row--inactive ' : '') + (indented ? 'aum-row--indented' : '')}>
      <td>
        <div className={"aum-user-cell" + (indented ? ' aum-user-cell--indented' : '')}>
          {indented && <div className="aum-indent-line" />}
          <div className={"aum-avatar" + (indented ? ' aum-avatar--sm' : '')}>{(user.name || '?')[0].toUpperCase()}</div>
          <div>
            <div className="aum-user-name">{user.name}</div>
            <div className="aum-user-email">{user.email}</div>
          </div>
        </div>
      </td>
      <td>
        <select className="aum-role-select" value={user.broker_level || 'adviser'} disabled={roleChanging === user.id} onChange={(e) => changeRole(user, e.target.value)}>
          {LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
        </select>
      </td>
      <td>
        <select className="aum-role-select" value={user.manager_id || ''} onChange={(e) => assignManager(user, e.target.value)}>
          <option value="">No manager</option>
          {allManagers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.broker_level})</option>)}
        </select>
      </td>
      <td>
        <div className="aum-status-cell">
          <StatusDot status={user.status} />
          <span className="aum-status-label">{user.status}</span>
        </div>
      </td>
      <td className="aum-date">{new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
      <td>
        <div className="aum-actions">
          <button className="aum-btn aum-btn--ghost" onClick={() => openReset(user)}>Reset PW</button>
          <button className={"aum-btn " + (user.status === 'active' ? 'aum-btn--danger' : 'aum-btn--success')} onClick={() => toggleStatus(user)}>
            {user.status === 'active' ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </td>
    </tr>
  );

  const TableWrap = ({ children }) => (
    <div className="aum-table-wrap">
      <table className="aum-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>Manager</th>
            <th>Status</th>
            <th>Joined</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );

  if (loading) return <div className="aum-loading">Loading users...</div>;

  return (
    <div className="bc-page-layout">
      <Sidebar />
      <main className="main-content">
        <div className="aum-wrap">
          <div className="aum-header">
            <div>
              <h1 className="aum-title">Team Users</h1>
              <p className="aum-subtitle">Manage your broker team — roles, access, and passwords</p>
            </div>
          </div>
          {error && <div className="aum-alert aum-alert--error">{error}</div>}
          {success && <div className="aum-alert aum-alert--success">{success}</div>}

          {/* Owners */}
          {owners.length > 0 && (
            <div className="aum-group">
              <div className="aum-group-header">
                <div><h2 className="aum-group-title">Owners</h2><p className="aum-group-desc">Full platform access</p></div>
                <span className="aum-group-count">{owners.length}</span>
              </div>
              <TableWrap>{owners.map(u => <UserRow key={u.id} user={u} />)}</TableWrap>
            </div>
          )}

          {/* Managers with advisers indented beneath */}
          {managersList.length > 0 && (
            <div className="aum-group">
              <div className="aum-group-header">
                <div><h2 className="aum-group-title">Managers & Their Advisers</h2><p className="aum-group-desc">Advisers are indented beneath their assigned manager</p></div>
                <span className="aum-group-count">{managersList.length + users.filter(u => (u.broker_level||'adviser')==='adviser' && u.manager_id).length}</span>
              </div>
              <TableWrap>
                {managersList.map(mgr => {
                  const advisers = users.filter(u => u.manager_id === mgr.id);
                  return (
                    <React.Fragment key={mgr.id}>
                      <UserRow user={mgr} />
                      {advisers.map(adv => <UserRow key={adv.id} user={adv} indented />)}
                      {advisers.length === 0 && (
                        <tr className="aum-row--indented">
                          <td colSpan="6" className="aum-no-advisers">No advisers assigned</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableWrap>
            </div>
          )}

          {/* Unassigned advisers */}
          {unassignedAdvisers.length > 0 && (
            <div className="aum-group">
              <div className="aum-group-header">
                <div><h2 className="aum-group-title">Unassigned Advisers</h2><p className="aum-group-desc">Not yet assigned to a manager</p></div>
                <span className="aum-group-count">{unassignedAdvisers.length}</span>
              </div>
              <TableWrap>{unassignedAdvisers.map(u => <UserRow key={u.id} user={u} />)}</TableWrap>
            </div>
          )}

          {users.length === 0 && <div className="aum-empty-state">No users found</div>}

          {resetModal && (
            <div className="aum-overlay" onClick={() => setResetModal(null)}>
              <div className="aum-modal" onClick={e => e.stopPropagation()}>
                <div className="aum-modal-header">
                  <h2 className="aum-modal-title">Reset Password</h2>
                  <button className="aum-modal-close" onClick={() => setResetModal(null)}>x</button>
                </div>
                <p className="aum-modal-desc">Set a new password for <strong>{resetModal.name}</strong>. They will be logged out of all sessions.</p>
                {resetError && <div className="aum-alert aum-alert--error">{resetError}</div>}
                <form onSubmit={handleReset}>
                  <div className="aum-pw-field">
                    <label className="aum-label">New password</label>
                    <div className="aum-pw-row">
                      <input type={showPw ? 'text' : 'password'} className="aum-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 8 characters" autoFocus />
                      <button type="button" className="aum-pw-toggle" onClick={() => setShowPw(s => !s)}>{showPw ? 'Hide' : 'Show'}</button>
                    </div>
                    {newPassword.length > 0 && (
                      <div className={"aum-pw-strength aum-" + strength}>
                        {strength === 'strong' ? 'Strong password' : strength === 'ok' ? 'Acceptable' : 'Too short'}
                      </div>
                    )}
                  </div>
                  <div className="aum-modal-actions">
                    <button type="button" className="aum-btn aum-btn--ghost" onClick={() => setResetModal(null)}>Cancel</button>
                    <button type="submit" className="aum-btn aum-btn--primary" disabled={resetSaving}>{resetSaving ? 'Resetting...' : 'Reset Password'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
